/**
 * Display formatting (U15). Client-facing document: PRICES only, rendered
 * verbatim from the frozen render model — no arithmetic beyond presentational
 * integer-cent sums for the alternates selector (never persisted, never
 * authoritative; the shared engine remains the only money math).
 */

const MONEY_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** "1234.50" → "$1,234.50" (decimal string in, display string out). */
export function fmtMoney(value: string): string {
  const num = Number(value);
  return Number.isFinite(num) ? MONEY_FMT.format(num) : value;
}

/** "3.0000" → "3" — trim trailing zeros from decimal qty strings. */
export function fmtQty(value: string): string {
  if (!/^-?\d+(\.\d+)?$/.test(value)) return value;
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}

export function fmtDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Date-only strings ("YYYY-MM-DD", e.g. invite due dates) must format as the
 * calendar day WRITTEN, not as UTC-midnight shifted into the local zone.
 */
export function fmtDateOnly(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return fmtDate(value);
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function fmtDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/** Exact 2-dp decimal string → integer cents (NaN-safe: returns null). */
export function toCents(value: string): number | null {
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(value);
  if (!match) return null;
  const sign = match[1] === '-' ? -1 : 1;
  const cents = Number(match[2]) * 100 + Number((match[3] ?? '0').padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? sign * cents : null;
}

/** Integer cents → "1234.56" decimal string for fmtMoney. */
export function centsToDecimal(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}
